import { Flags } from '@constants/flags';
import { WithdrawalMode } from '@generated/graphql';
import type { TranslatedString } from '@interfaces/shared';
import { useFlag } from '@onevesthq/feature-flags/web';
import { isEqual } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import { useFeatureFlagsResolved } from '../../../../../hooks/useFeatureFlag';
import {
  isFullTransfer,
  isWithdrawalFeatureActive,
  isWithdrawalModeSelectable,
  type NetFeesReport,
  resolveGrossCents,
  resolveWithdrawalMode,
} from './withdrawalMode.utils';

export type UseWithdrawalModeProp = {
  optionEnabled: boolean;
  accountId: string | undefined;
  frequency: string | null | undefined;
  scheduledDate: Date | string | null | undefined;
  isFutureDated: boolean;
  storedMode: WithdrawalMode | undefined;
  defaultMode: WithdrawalMode | undefined;
  netAmountCents: number | undefined;
  netAmountLabel: TranslatedString | undefined;
  storedNetAmountLabel: TranslatedString | undefined;
  amountCents: number | undefined;
  isWithdrawAll: boolean;
  isMultiCurrency: boolean;
  availableCents: number;
  handleValue: (value: Record<string, unknown>) => unknown;
};

export const useWithdrawalMode = ({
  optionEnabled,
  accountId,
  frequency,
  scheduledDate,
  isFutureDated,
  storedMode,
  defaultMode,
  netAmountCents,
  netAmountLabel,
  storedNetAmountLabel,
  amountCents,
  isWithdrawAll,
  isMultiCurrency,
  availableCents,
  handleValue,
}: UseWithdrawalModeProp) => {
  const { enabled: flagEnabled } = useFlag(Flags.withdrawal_net_gross, {
    default: false,
  });
  const flagsResolved = useFeatureFlagsResolved();
  const [netFees, setNetFees] = useState<NetFeesReport | null>(null);
  const previousEditorConfigRef = useRef({ defaultMode, optionEnabled });

  const selectableGates = {
    flagEnabled,
    optionEnabled,
    frequency,
    scheduledDate,
    isFutureDated,
    isMultiCurrency,
  };
  const isSelectable = isWithdrawalModeSelectable({
    ...selectableGates,
    isWithdrawAll,
  });
  // isWithdrawAll only hides the selector; it must not count as a reason to drop a stored Net.
  const isNetAvailable = isWithdrawalModeSelectable({
    ...selectableGates,
    isWithdrawAll: false,
  });
  const mode = resolveWithdrawalMode({
    selectable: isSelectable,
    storedMode,
    defaultMode,
  });
  const isNetMode = mode === WithdrawalMode.Net;
  const derivedGrossCents = resolveGrossCents({
    report: isNetMode ? netFees : null,
    accountId,
    netCents: netAmountCents,
  });
  // A net whose gross would reach the withdraw-all threshold is never written as a gross amount.
  const exceedsAvailable =
    derivedGrossCents !== undefined &&
    isFullTransfer(derivedGrossCents, availableCents);
  const grossPending =
    isNetMode && Boolean(netAmountCents) && derivedGrossCents === undefined;

  useEffect(() => {
    // Until the organization's flags load, the flag reads false and a saved Net
    // withdrawal would be wiped.
    if (!flagsResolved || storedMode !== WithdrawalMode.Net || isNetAvailable) {
      return;
    }

    // An explicit Gross, not undefined: undefined would fall back to a Net default
    // as soon as the form is selectable again and wipe the gross being typed.
    handleValue({
      withdrawalMode: WithdrawalMode.Gross,
      netAmountCents: undefined,
    });
  }, [flagsResolved, storedMode, isNetAvailable, handleValue]);

  // The edit-workflow preview keeps one form for its lifetime, so a changed editor default
  // must drop the selection stored by the previous one. Declared after the flags effect so this
  // reset wins over its gross write, and before the net-default write so a default Net still
  // gets stored when the toggle is turned back on in the same commit.
  useEffect(() => {
    const previous = previousEditorConfigRef.current;
    if (
      previous.defaultMode === defaultMode &&
      previous.optionEnabled === optionEnabled
    ) {
      return;
    }

    previousEditorConfigRef.current = { defaultMode, optionEnabled };
    // A form that never had the feature on stores no mode, and clearing amountCents would wipe its gross.
    const isFeatureActive = isWithdrawalFeatureActive({
      flagEnabled,
      optionEnabled: previous.optionEnabled || optionEnabled,
      storedMode,
      netAmountCents,
    });
    if (!isFeatureActive) return;

    handleValue({
      withdrawalMode: undefined,
      netAmountCents: undefined,
      amountCents: undefined,
    });
  }, [
    defaultMode,
    optionEnabled,
    flagEnabled,
    storedMode,
    netAmountCents,
    handleValue,
  ]);

  useEffect(() => {
    if (!isSelectable || storedMode !== undefined || !isNetMode) return;

    // The review step reads the stored mode, so a default Net must be written out.
    handleValue({ withdrawalMode: WithdrawalMode.Net });
  }, [isSelectable, storedMode, isNetMode, handleValue]);

  useEffect(() => {
    if (!isNetMode) return;

    const contextGrossCents = exceedsAvailable ? undefined : derivedGrossCents;
    if (contextGrossCents === amountCents) return;

    handleValue({ amountCents: contextGrossCents });
  }, [
    isNetMode,
    derivedGrossCents,
    exceedsAvailable,
    amountCents,
    handleValue,
  ]);

  useEffect(() => {
    if (!isNetMode || isEqual(netAmountLabel, storedNetAmountLabel)) return;

    // The review step has no netAmountLabel option of its own, so it reads the form's from context.
    handleValue({ netAmountLabel });
  }, [isNetMode, netAmountLabel, storedNetAmountLabel, handleValue]);

  const selectMode = (nextMode: WithdrawalMode) => {
    if (nextMode === mode) return;

    handleValue({
      withdrawalMode: nextMode,
      amountCents: undefined,
      netAmountCents: undefined,
    });
  };

  const setNetAmountCents = (cents: number | undefined) =>
    handleValue({ netAmountCents: cents });

  return {
    isSelectable,
    mode,
    grossPending,
    exceedsAvailable,
    selectMode,
    setNetAmountCents,
    onNetFeesChange: setNetFees,
  };
};
